/**
 * Public port lists (PE-920). The jumphost gateway forwards an explicit set of
 * ports - individual ports and/or contiguous ranges - over both TCP and UDP. A
 * port list is therefore a set of port specs, each either a single port (`443`)
 * or an inclusive range (`8000-9000`). This module is the single source of truth
 * for parsing/normalizing the comma-separated input the customer types and is
 * shared by client and server so both validate the same way.
 */

/** Upper bound on the number of specs in a single list - bounded so a list stays sane. */
export const MAX_PORTS_PER_LIST = 100;

/** Inclusive bounds for a valid port number. */
export const MIN_PORT = 1;
export const MAX_PORT = 65535;

/**
 * A normalized port spec: a single port or an inclusive range. Stored and shown
 * as a string token ("443" or "8000-9000") - the exact form the gateway config
 * consumes (it normalizes the hyphen to `:` for iptables).
 */
export type ParsePortsResult = { ok: true; ports: string[] } | { ok: false; reason: string };

interface Interval {
  start: number;
  end: number;
}

function isValidPort(n: number): boolean {
  return Number.isInteger(n) && n >= MIN_PORT && n <= MAX_PORT;
}

/** Parse one token into an inclusive interval, or return a friendly reason. */
function parseToken(
  token: string,
): { ok: true; interval: Interval } | { ok: false; reason: string } {
  const range = token.match(/^(\d+)\s*-\s*(\d+)$/);
  if (range) {
    const start = Number(range[1]);
    const end = Number(range[2]);
    if (!isValidPort(start) || !isValidPort(end)) {
      return {
        ok: false,
        reason: `Ports must be between ${MIN_PORT} and ${MAX_PORT} (got "${token}").`,
      };
    }
    if (start > end) {
      return { ok: false, reason: `Range start must not exceed its end (got "${token}").` };
    }
    return { ok: true, interval: { start, end } };
  }

  if (!/^\d+$/.test(token)) {
    return { ok: false, reason: `"${token}" is not a valid port or range.` };
  }
  const port = Number(token);
  if (!isValidPort(port)) {
    return {
      ok: false,
      reason: `Ports must be between ${MIN_PORT} and ${MAX_PORT} (got "${token}").`,
    };
  }
  return { ok: true, interval: { start: port, end: port } };
}

/** Format an interval back to its canonical token form. */
function formatInterval({ start, end }: Interval): string {
  return start === end ? String(start) : `${start}-${end}`;
}

/**
 * Merge overlapping/adjacent intervals into a sorted, minimal set. Adjacent
 * intervals (e.g. 80-90 and 91-100) collapse into one (80-100).
 */
function mergeIntervals(intervals: Interval[]): Interval[] {
  const sorted = [...intervals].sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: Interval[] = [];
  for (const cur of sorted) {
    const last = merged[merged.length - 1];
    if (last && cur.start <= last.end + 1) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}

/**
 * Parse a comma-separated list of ports and/or inclusive ranges into a
 * normalized, deduped, ascending set of canonical tokens (overlapping/adjacent
 * ranges merged). Returns a friendly reason on the first problem.
 */
export function parsePortList(input: string): ParsePortsResult {
  const tokens = input
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  if (tokens.length === 0) {
    return { ok: false, reason: "Enter at least one port." };
  }

  const intervals: Interval[] = [];
  for (const token of tokens) {
    const parsed = parseToken(token);
    if (!parsed.ok) return parsed;
    intervals.push(parsed.interval);
  }

  const merged = mergeIntervals(intervals);
  if (merged.length > MAX_PORTS_PER_LIST) {
    return { ok: false, reason: `A list can have at most ${MAX_PORTS_PER_LIST} ports or ranges.` };
  }

  return { ok: true, ports: merged.map(formatInterval) };
}

/** Render normalized port tokens back to the comma-separated form we display. */
export function formatPorts(ports: readonly string[]): string {
  return ports.join(", ");
}

/** Total number of individual ports the tokens cover (ranges expanded). */
export function countPorts(ports: readonly string[]): number {
  let total = 0;
  for (const token of ports) {
    const range = token.match(/^(\d+)-(\d+)$/);
    total += range ? Number(range[2]) - Number(range[1]) + 1 : 1;
  }
  return total;
}

/**
 * Merge port tokens from several lists into one normalized set - used to compute
 * a node's effective open ports across all the lists mapped onto it.
 */
export function mergePortTokens(lists: readonly (readonly string[])[]): string[] {
  const intervals: Interval[] = [];
  for (const list of lists) {
    for (const token of list) {
      const range = token.match(/^(\d+)-(\d+)$/);
      if (range) intervals.push({ start: Number(range[1]), end: Number(range[2]) });
      else intervals.push({ start: Number(token), end: Number(token) });
    }
  }
  return mergeIntervals(intervals).map(formatInterval);
}

/**
 * Whether two token arrays cover the exact same set of ports, regardless of how
 * they're written (`["80","81"]` and `["80-81"]` are equal). Compares canonical
 * forms so pre-canonicalized stored data (e.g. rows migrated from the old
 * individual-port model) doesn't read as a change against a freshly parsed list.
 */
export function samePortSets(a: readonly string[], b: readonly string[]): boolean {
  const na = mergePortTokens([a]);
  const nb = mergePortTokens([b]);
  return na.length === nb.length && na.every((p, i) => p === nb[i]);
}
